import { useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import {
  cargarPerfilDeSesion,
  esAdministrador,
  estaDesactivado,
  guardarCacheUsuario,
  leerCacheUsuario,
  limpiarSesionLocal
} from '../utils/sesion';

/**
 * 🔐 Estado de la sesión del panel.
 *
 * `CARGANDO` es el estado inicial obligatorio: `onAuthStateChanged` tarda un
 * instante en resolver si hay sesión, y sin ese estado el panel redirigiría a
 * `/login` en cada recarga aunque el usuario esté autenticado.
 */
export const ESTADO_SESION = {
  CARGANDO: 'cargando',
  SIN_SESION: 'sin-sesion',
  // Hay cuenta en Firebase Auth pero no tiene ficha en `usuarios/{uid}`,
  // la ficha está desactivada, o no es de administrador
  SIN_PERFIL: 'sin-perfil',
  // Firestore no contestó: no se puede saber el rol, así que no se entra
  ERROR: 'error',
  ACTIVA: 'activa'
};

/**
 * Sesión real del panel: Firebase Auth manda y el rol sale de Firestore.
 * Ver `utils/sesion.js` para el porqué del cambio.
 */
export function useSesion() {
  const [estado, setEstado] = useState(ESTADO_SESION.CARGANDO);
  // La caché solo se usa para no pintar la cabecera vacía mientras Firestore
  // responde; el estado no pasa a ACTIVA hasta que llega el perfil real.
  const [usuario, setUsuario] = useState(() => leerCacheUsuario());
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    let vigente = true;

    const cancelar = onAuthStateChanged(auth, async (cuenta) => {
      if (!cuenta) {
        limpiarSesionLocal();
        if (!vigente) return;
        setUsuario(null);
        setEstado(ESTADO_SESION.SIN_SESION);
        return;
      }

      try {
        const perfil = await cargarPerfilDeSesion(cuenta);
        if (!vigente) return;

        if (!perfil) {
          setUsuario(null);
          setMotivo('Tu cuenta no tiene ficha en la lista de usuarios. Pide a un administrador que la cree.');
          setEstado(ESTADO_SESION.SIN_PERFIL);
          return;
        }

        if (estaDesactivado(perfil)) {
          setUsuario(null);
          setMotivo('Tu usuario está desactivado. Contacta con un administrador.');
          setEstado(ESTADO_SESION.SIN_PERFIL);
          return;
        }

        // 🔒 El panel es solo de administradores. Antes bastaba con tener
        // ficha activa: un operario entraba con sus credenciales de la APK y
        // podía crear formularios o editar respuestas (solo se le ocultaba
        // Usuarios). La barrera de verdad son las reglas de Firestore
        // (`firestore.rules`); esto es lo que evita que la pantalla se lo ofrezca.
        if (!esAdministrador(perfil)) {
          setUsuario(null);
          setMotivo('El panel es solo para administradores. Usa la app IGNIA en el móvil.');
          setEstado(ESTADO_SESION.SIN_PERFIL);
          return;
        }

        guardarCacheUsuario(perfil);
        setUsuario(perfil);
        setEstado(ESTADO_SESION.ACTIVA);
      } catch (error) {
        console.error('No se pudo comprobar el perfil del usuario:', error);
        if (!vigente) return;
        // Sin perfil no hay rol, y sin rol no se entra: la caché local no
        // puede conceder permisos que Firestore no ha confirmado.
        setMotivo('No se pudo verificar tu perfil. Revisa la conexión e inténtalo de nuevo.');
        setEstado(ESTADO_SESION.ERROR);
      }
    });

    return () => {
      vigente = false;
      cancelar();
    };
  }, []);

  return {
    estado,
    usuario,
    motivo,
    esAdmin: estado === ESTADO_SESION.ACTIVA && esAdministrador(usuario)
  };
}

/** Cierra la sesión de Firebase y borra solo las claves de sesión. */
export async function cerrarSesion() {
  limpiarSesionLocal();
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error al cerrar la sesión de Firebase:', error);
  }
}
